import React from 'react';
import { RootState, AppDispatch } from '../store';
import { useSelector, useDispatch } from 'react-redux';
import {
	Orientation,
	Platform,
	PopOver,
	WriteDirection,
	TranslationsKey,
	PopOverSceneItemData,
	ArmrestsByCover,
	CoverItem,
} from 'sofa-reborn-react';
import Popover from '../components/Popover';
import { hidePopOver } from '../store/popOver/actions';
import { localeService } from '../services';
import { removeSceneItem, setKeepModule } from '../store/scene/actions';
import { setUnfinishedModule } from '../store/missing/actions';
import { ProductData } from '@ikea-aoa/component-view-3d';

const getTop = (
	platform: Platform,
	orientation: Orientation,
	popOver: PopOver,
	hasArmrestPicker: boolean
): string => {
	if (orientation === 'portrait') {
		return 'auto';
	} else {
		if (
			popOver.type === 'middle-module-drag' ||
			popOver.type === 'unfinished-section' ||
			popOver.type === 'missing-cover-section' ||
			popOver.type === 'invalid-placement' ||
			popOver.type === 'no-valid-connection' ||
			popOver.type === 'disabled-drag' ||
			popOver.type === 'placement-information'
		) {
			return popOver.top + 'px';
		}

		/* Build-limit */
		return platform === 'kiosk'
			? hasArmrestPicker
				? '310px'
				: '230px'
			: hasArmrestPicker
			? '240px'
			: '200px';
	}
};

const getLeft = (
	orientation: Orientation,
	writeDirection: WriteDirection,
	popOver: PopOver,
	inlineMessageWidth: number
): string => {
	if (orientation === 'portrait') {
		return window.innerWidth / 2 - inlineMessageWidth / 2 + 'px';
	} else {
		if (
			popOver.type === 'middle-module-drag' ||
			popOver.type === 'unfinished-section' ||
			popOver.type === 'missing-cover-section' ||
			popOver.type === 'invalid-placement' ||
			popOver.type === 'no-valid-connection' ||
			popOver.type === 'placement-information'
		) {
			return popOver.left + 'px';
		} else if (popOver.type === 'disabled-drag') {
			return (popOver.left ? popOver.left - inlineMessageWidth : 0) + 'px';
		}

		/* Build-limit */
		const selectorWidth = document.getElementById('selector')?.clientWidth || 0;
		const swiperWidth = selectorWidth - 10;
		const leftOfScene = document.getElementById('scene')?.getBoundingClientRect().left || 0;
		return window.innerWidth - (swiperWidth + leftOfScene) - inlineMessageWidth + 'px';
	}
};

const getBottom = (orientation: Orientation): string => {
	if (orientation === 'portrait') {
		const footerHeight = document.getElementById('footer')?.clientHeight || 0;
		const selectorHeight = document.getElementById('selector')?.clientHeight || 0;
		return footerHeight + selectorHeight + 'px';
	} else {
		return 'auto';
	}
};

const onKeepUnfinished = (dispatch: AppDispatch): void => {
	dispatch(hidePopOver({ type: 'no-valid-connection' }));
	dispatch(setUnfinishedModule(null));
	dispatch(setKeepModule(true));
};

const onRemoveUnfinished = (dispatch: AppDispatch, sceneItem: PopOverSceneItemData): void => {
	dispatch(removeSceneItem(sceneItem));
	dispatch(hidePopOver({ type: 'no-valid-connection' }));
	dispatch(setUnfinishedModule(null));
};

const onClose = (dispatch: AppDispatch, popover: PopOver): void => {
	dispatch(hidePopOver({ type: popover.type }));
};

const PopoverContainer: React.FC<{ popOver: PopOver }> = (props) => {
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const productData = useSelector<RootState, ProductData>((state) => state.products.products);
	const unfinishedModule = useSelector<RootState, PopOverSceneItemData>(
		(state) => state.missing.unfinishedModule as PopOverSceneItemData
	);
	const previousDraggedItemId = useSelector<RootState, string | null>(
		(state) => state.swiper.previousDraggedItemId
	);
	const armrestsByCover = useSelector<RootState, ArmrestsByCover | null>(
		(state) => state.armrest.armrests
	);
	const selectedCover = useSelector<RootState, CoverItem | null>(
		(state) => state.cover.selectedCover
	);
	const missingCoverSections = useSelector<RootState, PopOverSceneItemData[]>(
		(state) => state.missing.missingCoverSections
	);
	const hasArmrestPicker = armrestsByCover ? true : false;
	let interfereWithFootstool = false;
	if (previousDraggedItemId) {
		const previousItemFamily = productData[previousDraggedItemId].filter.family;
		if (previousItemFamily) {
			interfereWithFootstool = previousItemFamily === 'FOOTSTOOL';
		}
	}
	const extendedWidth =
		props.popOver.type === 'missing-cover-section' || props.popOver.type === 'no-valid-connection';
	const inlineMessageWidth = platform === 'kiosk' ? 410 : extendedWidth ? 340 : 280;
	const dispatch = useDispatch<AppDispatch>();
	const popOver = props.popOver;
	const translations = {
		'disabled-drag': localeService.getTranslation('itemCantFitInDesign'),
		'build-limit': localeService.getTranslation('tooltipBuildLimit'),
		'middle-module-drag': localeService.getTranslation('invaldDragInBetweenModule'),
		'unfinished-section': localeService.getTranslation('unfinishedCornerSection'),
		'missing-cover-section': localeService
			.getTranslation('missingCoverNotice')
			.replace('[[covername]]', selectedCover?.name || '')
			.replace(
				'[[modulename]]',
				missingCoverSections.length > 0 && missingCoverSections[0].moduleName
					? missingCoverSections[0].moduleName
					: ''
			),
		'invalid-placement': localeService.getTranslation(popOver.translationKey as TranslationsKey),
		'placement-information': localeService.getTranslation(
			popOver.translationKey as TranslationsKey
		),
		'no-valid-connection': interfereWithFootstool
			? localeService.getTranslation('popupCornerInterfereFootstool')
			: localeService.getTranslation('popupCornerInterfereSofaBed'),
	};

	return (
		<Popover
			platform={platform}
			writeDirection={writeDirection}
			top={getTop(platform, orientation, popOver, hasArmrestPicker)}
			left={getLeft(orientation, writeDirection, popOver, inlineMessageWidth)}
			bottom={getBottom(orientation)}
			text={translations[props.popOver.type]}
			extendWidth={extendedWidth}
			alert={
				props.popOver.type === 'unfinished-section' ||
				props.popOver.type === 'missing-cover-section'
			}
			negative={props.popOver.type === 'disabled-drag'}
			showButtons={props.popOver.type === 'no-valid-connection'}
			onKeepUnfinished={(): void => onKeepUnfinished(dispatch)}
			onRemoveUnfinished={(): void => onRemoveUnfinished(dispatch, unfinishedModule)}
			onClose={(): void => onClose(dispatch, popOver)}
		/>
	);
};

export default PopoverContainer;
